import { useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import type {
  HealthBand,
  HealthComponentBreakdown,
} from "../../types/crm";
import { HealthBreakdownPopover } from "./HealthBreakdownPopover";

// Rough popover dimensions used to decide flip direction before the real
// element is measured. Popover has `w-80` (320px) and ~280px height with
// 6 signal rows — these are upper bounds, not exact.
const APPROX_POPOVER_W = 320;
const APPROX_POPOVER_H = 320;
const GAP = 6; // px between chip and popover
const EDGE_PAD = 8; // px min distance from viewport edge

const BAND_LABELS: Record<HealthBand, string> = {
  healthy: "Healthy",
  watch: "Watch",
  concerning: "Concerning",
  at_risk: "At Risk",
};

interface Props {
  band: HealthBand | null;
  score: number | null;
  /** When provided, hovering the chip reveals the 6-signal breakdown popover. */
  breakdown?: HealthComponentBreakdown | null;
  className?: string;
}

/**
 * Compact chip showing health band + numeric score. When a breakdown is
 * provided, renders a fixed-positioned popover on hover (mirrors
 * FunnelSummaryPopover pattern in billing/Billing.tsx).
 */
export function HealthBandChip({ band, score, breakdown, className }: Props) {
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  /**
   * Placement algorithm — flips up when there's no room below, flips left
   * when there's no room to the right. Uses approximate popover dimensions
   * on first paint, then (via useLayoutEffect below) re-measures against
   * the real element and re-anchors if the estimate was off.
   */
  const computePosition = (
    rect: DOMRect,
    popH = APPROX_POPOVER_H,
    popW = APPROX_POPOVER_W,
  ): { top: number; left: number } => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const roomBelow = vh - rect.bottom;
    const roomAbove = rect.top;
    const top =
      roomBelow >= popH + GAP + EDGE_PAD || roomBelow >= roomAbove
        ? // below
          rect.bottom + GAP
        : // above
          Math.max(EDGE_PAD, rect.top - popH - GAP);

    const roomRight = vw - rect.left;
    const left =
      roomRight >= popW + EDGE_PAD
        ? rect.left
        : // flip so popover right-aligns with chip right edge
          Math.max(EDGE_PAD, rect.right - popW);
    return { top, left };
  };

  // Re-measure the popover once it's actually in the DOM; if the real size
  // differs from the approximation the initial position used, re-anchor.
  // Must run unconditionally (rules-of-hooks) — the `!hovered` guard keeps
  // it a no-op when the popover isn't open.
  useLayoutEffect(() => {
    if (!hovered) return;
    const anchor = anchorRef.current;
    const pop = popoverRef.current;
    if (!anchor || !pop) return;
    const rect = anchor.getBoundingClientRect();
    const popH = pop.offsetHeight;
    const popW = pop.offsetWidth;
    const next = computePosition(rect, popH, popW);
    setPos((prev) => {
      if (prev && prev.top === next.top && prev.left === next.left) return prev;
      return next;
    });
  }, [hovered]);

  if (!band) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const handleEnter = () => {
    if (!breakdown) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(computePosition(rect));
    setHovered(true);
  };
  const handleLeave = () => setHovered(false);

  // Bar color reflects the score itself, mapped to the autumn-frontend
  // brand palette (teal for healthy, pink for at-risk) with warm
  // intermediates for watch/concerning. Per directive 2026-04-26: avoid
  // strong default-tailwind greens / reds / blues / purples.
  const barClasses =
    band === "healthy"
      ? "from-brand-primary-light to-brand-primary"
      : band === "watch"
        ? "from-amber-200 to-amber-300"
        : band === "concerning"
          ? "from-orange-300 to-orange-400"
          : "from-brand-secondary-light to-brand-secondary";

  return (
    <>
      <div
        ref={anchorRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className={clsx(
          "inline-flex items-center gap-1 align-middle",
          breakdown && "cursor-help",
          className,
        )}
      >
        <div
          className={clsx(
            "relative w-16 h-5 rounded-md overflow-hidden border border-black/10 bg-gradient-to-r",
            barClasses,
          )}
        >
          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-content-primary">
            {score != null ? score : "—"}
          </span>
        </div>
      </div>
      {hovered && pos && breakdown && (
        <div
          ref={popoverRef}
          className="fixed z-50 pointer-events-none"
          style={{ top: pos.top, left: pos.left }}
        >
          <HealthBreakdownPopover
            breakdown={breakdown}
            headline={
              score != null
                ? `Score: ${score} (${BAND_LABELS[band]})`
                : BAND_LABELS[band]
            }
          />
        </div>
      )}
    </>
  );
}
